// Integrations: the catalog of outside web services Bloom apps may read from (docs/integrations.md). Apps never get
// network access; they ask the server's room, which checks the call against this catalog (and the server's settings,
// the app's manifest, throttles), makes it with the server's own key and hands back the endpoint's `shape`d result.
//
// Catalog only: an app calls only endpoints declared here, with typed parameters. Official APIs only; no OAuth yet.
// Each endpoint's `example` is what it returns (in that shape): the build agent's guide shows it, and checks and the
// headless view check answer with it, so building an app never touches the network.

export type IntegrationParamType = "text" | "number" | "choice";

export interface IntegrationParam {
  key: string;
  type: IntegrationParamType;
  description: string;
  required?: boolean;
  default?: string | number;
  /** text */
  maxLength?: number;
  pattern?: string;
  upper?: boolean;
  lower?: boolean;
  /** number */
  min?: number;
  max?: number;
  integer?: boolean;
  /** choice */
  options?: string[];
}

export type ParamValues = Record<string, string | number>;

export interface IntegrationEndpoint {
  description: string;
  params: IntegrationParam[];
  /** The URL; `{key}` is replaced by that param (URL-encoded). Params not in the path go through `query`. */
  url: string;
  /** The query string to send (default: every param not in the path, as is). `now`: ms (for date ranges). */
  query?: (p: ParamValues, now: number) => Record<string, string | number>;
  /** How long an answer may be reused, in seconds. */
  cacheSeconds: number;
  /** Turns the service's JSON into the documented result. Throwing means the service answered nonsense. */
  shape: (raw: unknown, p: ParamValues) => unknown;
  /** What a 404 means, if it means "nothing found" rather than an error. */
  notFound?: (p: ParamValues) => unknown;
  /** Params for the example below, and the example result (in `shape`'s form). */
  exampleParams: ParamValues;
  example: unknown;
}

export type IntegrationAuth =
  | { type: "none" }
  | {
      type: "api_key";
      /** Where the key goes: a request header (preferred: keys stay out of URLs) or a query parameter. */
      header?: string;
      query?: string;
      /** Where to get one, and what to do there. */
      signup: string;
      help: string;
      /** What a key looks like (checked when saved). */
      pattern?: string;
    };

export interface Integration {
  id: string;
  name: string;
  icon: string;
  description: string;
  homepage: string;
  terms: string;
  auth: IntegrationAuth;
  /** Most upstream calls per server per minute (under the provider's own free limit). */
  perMinute: number;
  endpoints: Record<string, IntegrationEndpoint>;
  /** The cheap call Settings' Test button makes. */
  test: { endpoint: string; params: ParamValues };
}

// ---- Helpers for shapes ----

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
const str = (v: unknown, max = 500): string | null => (typeof v === "string" && v ? v.slice(0, max) : null);
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const round = (v: unknown, digits = 2): number | null => {
  const n = num(v);
  return n === null ? null : Math.round(n * 10 ** digits) / 10 ** digits;
};
const ymd = (ms: number) => new Date(ms).toISOString().slice(0, 10);
const ENTITIES: Record<string, string> = { amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", nbsp: " " };
/** HTML (Wiktionary's definitions) as plain text: tags dropped, entities decoded, spaces collapsed. */
const plainText = (v: unknown, max: number): string | null => {
  if (typeof v !== "string") return null;
  const t = v
    .replace(/<[^>]*>/g, "")
    .replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (m, e: string) => {
      if (e[0] === "#") {
        const n = e[1] === "x" || e[1] === "X" ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
        return Number.isFinite(n) && n > 0 && n < 0x110000 ? String.fromCodePoint(n) : "";
      }
      return ENTITIES[e.toLowerCase()] ?? m;
    })
    .replace(/\s+/g, " ")
    .trim();
  return t ? t.slice(0, max) : null;
};

/** WMO weather codes (Open-Meteo's weather_code) in words. */
export const WEATHER_CODES: Record<number, string> = {
  0: "Clear", 1: "Mostly clear", 2: "Partly cloudy", 3: "Overcast", 45: "Fog", 48: "Freezing fog",
  51: "Light drizzle", 53: "Drizzle", 55: "Heavy drizzle", 56: "Freezing drizzle", 57: "Freezing drizzle",
  61: "Light rain", 63: "Rain", 65: "Heavy rain", 66: "Freezing rain", 67: "Freezing rain",
  71: "Light snow", 73: "Snow", 75: "Heavy snow", 77: "Snow grains", 80: "Light showers", 81: "Showers",
  82: "Heavy showers", 85: "Snow showers", 86: "Heavy snow showers", 95: "Thunderstorm", 96: "Thunderstorm with hail",
  99: "Thunderstorm with hail",
};
const conditions = (code: unknown) => (num(code) !== null ? (WEATHER_CODES[num(code)!] ?? "Unknown") : null);

// ---- The catalog ----

const weather: Integration = {
  id: "weather",
  name: "Open-Meteo weather",
  icon: "🌤️",
  description: "Forecasts and current conditions anywhere, and looking up places by name",
  homepage: "https://open-meteo.com",
  terms: "https://open-meteo.com/en/terms",
  auth: { type: "none" },
  perMinute: 60,
  test: { endpoint: "geocode", params: { name: "Boston" } },
  endpoints: {
    forecast: {
      description: "Current conditions and a daily forecast for a place (latitude and longitude: use geocode to find them)",
      url: "https://api.open-meteo.com/v1/forecast",
      params: [
        { key: "latitude", type: "number", required: true, min: -90, max: 90, description: "Latitude" },
        { key: "longitude", type: "number", required: true, min: -180, max: 180, description: "Longitude" },
        { key: "days", type: "number", integer: true, min: 1, max: 16, default: 7, description: "Days of forecast (1–16)" },
        { key: "units", type: "choice", options: ["imperial", "metric"], default: "imperial", description: "°F and mph, or °C and km/h" },
      ],
      query: (p) => ({
        latitude: round(p.latitude, 4)!,
        longitude: round(p.longitude, 4)!,
        current: "temperature_2m,apparent_temperature,relative_humidity_2m,weather_code,wind_speed_10m,is_day",
        daily: "weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max,sunrise,sunset",
        timezone: "auto",
        forecast_days: p.days!,
        ...(p.units === "imperial" ? { temperature_unit: "fahrenheit", wind_speed_unit: "mph", precipitation_unit: "inch" } : {}),
      }),
      cacheSeconds: 900,
      shape: (raw, p) => {
        if (!isObj(raw)) throw new Error("not an object");
        const cur = isObj(raw.current) ? raw.current : {};
        const d = isObj(raw.daily) ? raw.daily : {};
        const dates = arr(d.time);
        return {
          timezone: str(raw.timezone, 60),
          units: p.units === "imperial" ? { temperature: "°F", wind: "mph" } : { temperature: "°C", wind: "km/h" },
          current: {
            time: str(cur.time, 30),
            temperature: round(cur.temperature_2m, 1),
            feelsLike: round(cur.apparent_temperature, 1),
            humidity: num(cur.relative_humidity_2m),
            wind: round(cur.wind_speed_10m, 1),
            code: num(cur.weather_code),
            conditions: conditions(cur.weather_code),
            isDay: cur.is_day === 1,
          },
          daily: dates.slice(0, 16).map((t, i) => ({
            date: str(t, 10),
            high: round(arr(d.temperature_2m_max)[i], 1),
            low: round(arr(d.temperature_2m_min)[i], 1),
            rainChance: num(arr(d.precipitation_probability_max)[i]),
            code: num(arr(d.weather_code)[i]),
            conditions: conditions(arr(d.weather_code)[i]),
            sunrise: str(arr(d.sunrise)[i], 20),
            sunset: str(arr(d.sunset)[i], 20),
          })),
        };
      },
      exampleParams: { latitude: 42.36, longitude: -71.06, days: 2, units: "imperial" },
      example: {
        timezone: "America/New_York",
        units: { temperature: "°F", wind: "mph" },
        current: { time: "2026-10-24T14:00", temperature: 58.3, feelsLike: 55.9, humidity: 61, wind: 9.4, code: 2, conditions: "Partly cloudy", isDay: true },
        daily: [
          { date: "2026-10-24", high: 61.2, low: 47.5, rainChance: 10, code: 2, conditions: "Partly cloudy", sunrise: "2026-10-24T07:13", sunset: "2026-10-24T17:44" },
          { date: "2026-10-25", high: 55.0, low: 44.1, rainChance: 70, code: 63, conditions: "Rain", sunrise: "2026-10-25T07:14", sunset: "2026-10-25T17:42" },
        ],
      },
    },
    geocode: {
      description: "Find places by name (cities, towns): coordinates for forecast",
      url: "https://geocoding-api.open-meteo.com/v1/search",
      params: [
        { key: "name", type: "text", required: true, maxLength: 80, description: "Place name (\"Boston\", \"Palermo\")" },
        { key: "count", type: "number", integer: true, min: 1, max: 10, default: 5, description: "How many matches" },
      ],
      query: (p) => ({ name: p.name!, count: p.count!, language: "en", format: "json" }),
      cacheSeconds: 86400,
      shape: (raw) => {
        if (!isObj(raw)) throw new Error("not an object");
        return {
          places: arr(raw.results).filter(isObj).slice(0, 10).map((r) => ({
            name: str(r.name, 100),
            region: str(r.admin1, 100),
            country: str(r.country, 100),
            countryCode: str(r.country_code, 4),
            latitude: round(r.latitude, 4),
            longitude: round(r.longitude, 4),
            timezone: str(r.timezone, 60),
            population: num(r.population),
          })),
        };
      },
      exampleParams: { name: "Boston", count: 1 },
      example: { places: [{ name: "Boston", region: "Massachusetts", country: "United States", countryCode: "US", latitude: 42.3584, longitude: -71.0598, timezone: "America/New_York", population: 617594 }] },
    },
  },
};

const dictionary: Integration = {
  id: "dictionary",
  name: "Wiktionary",
  icon: "📖",
  description: "English definitions and examples from Wiktionary, the free dictionary run by Wikimedia",
  homepage: "https://en.wiktionary.org",
  terms: "https://foundation.wikimedia.org/wiki/Policy:Terms_of_Use",
  auth: { type: "none" },
  perMinute: 60,
  test: { endpoint: "define", params: { word: "bloom" } },
  endpoints: {
    define: {
      description: "Definitions of an English word, by part of speech, with examples ({ found: false } when Wiktionary has none). Text is from Wiktionary (CC BY-SA): show `url` as the source",
      url: "https://en.wiktionary.org/api/rest_v1/page/definition/{word}",
      params: [{ key: "word", type: "text", required: true, maxLength: 40, lower: true, pattern: "^[a-zA-Z][a-zA-Z' -]*$", description: "The word" }],
      query: () => ({}),
      cacheSeconds: 86400,
      notFound: (p) => ({ found: false, word: p.word }),
      shape: (raw, p) => {
        // Keyed by language code; we only ask for English entries.
        const groups = isObj(raw) ? arr(raw.en).filter(isObj) : [];
        const meanings = groups
          .map((g) => ({
            partOfSpeech: str(g.partOfSpeech, 30),
            definitions: arr(g.definitions)
              .filter(isObj)
              .map((d) => {
                const parsed = arr(d.parsedExamples).filter(isObj).map((x) => x.example)[0];
                return { definition: plainText(d.definition, 400), example: plainText(parsed ?? arr(d.examples)[0], 300) };
              })
              .filter((d) => d.definition)
              .slice(0, 3),
          }))
          .filter((m) => m.definitions.length)
          .slice(0, 6);
        if (!meanings.length) return { found: false, word: p.word };
        return { found: true, word: p.word, meanings, source: "Wiktionary", url: `https://en.wiktionary.org/wiki/${encodeURIComponent(String(p.word).replace(/ /g, "_"))}` };
      },
      exampleParams: { word: "bloom" },
      example: {
        found: true,
        word: "bloom",
        meanings: [
          { partOfSpeech: "Noun", definitions: [{ definition: "A blossom; the flower of a plant.", example: "The tree is covered in pink blooms." }] },
          { partOfSpeech: "Verb", definitions: [{ definition: "To cause to blossom or flourish.", example: null }] },
        ],
        source: "Wiktionary",
        url: "https://en.wiktionary.org/wiki/bloom",
      },
    },
  },
};

const finnhub: Integration = {
  id: "finnhub",
  name: "Finnhub",
  icon: "📈",
  description: "Stock quotes, symbol search, company profiles and company news (US markets)",
  homepage: "https://finnhub.io",
  terms: "https://finnhub.io/terms-of-service",
  auth: {
    type: "api_key",
    header: "X-Finnhub-Token",
    signup: "https://finnhub.io/register",
    help: "Sign up for a free Finnhub account, then copy the API key shown on your dashboard.",
    pattern: "^[A-Za-z0-9]{16,64}$",
  },
  perMinute: 50,
  test: { endpoint: "quote", params: { symbol: "AAPL" } },
  endpoints: {
    quote: {
      description: "The latest price of a stock (found: false for an unknown symbol). Prices are in USD.",
      url: "https://finnhub.io/api/v1/quote",
      params: [{ key: "symbol", type: "text", required: true, maxLength: 12, upper: true, pattern: "^[A-Za-z][A-Za-z0-9.\\-]*$", description: "Ticker symbol (\"NVDA\")" }],
      cacheSeconds: 30,
      shape: (raw, p) => {
        if (!isObj(raw)) throw new Error("not an object");
        const found = !(num(raw.c) === 0 && (num(raw.t) ?? 0) === 0);
        if (!found) return { found: false, symbol: p.symbol };
        return {
          found: true,
          symbol: p.symbol,
          price: round(raw.c, 4),
          change: round(raw.d, 4),
          percent: round(raw.dp, 3),
          high: round(raw.h, 4),
          low: round(raw.l, 4),
          open: round(raw.o, 4),
          previousClose: round(raw.pc, 4),
          time: num(raw.t) !== null ? num(raw.t)! * 1000 : null,
        };
      },
      exampleParams: { symbol: "NVDA" },
      example: { found: true, symbol: "NVDA", price: 181.24, change: 2.39, percent: 1.336, high: 182.5, low: 178.1, open: 179.0, previousClose: 178.85, time: 1793030400000 },
    },
    search: {
      description: "Find US stock symbols by company name or symbol",
      url: "https://finnhub.io/api/v1/search",
      params: [{ key: "query", type: "text", required: true, maxLength: 40, description: "Name or symbol (\"nvidia\")" }],
      query: (p) => ({ q: p.query!, exchange: "US" }),
      cacheSeconds: 3600,
      shape: (raw) => {
        if (!isObj(raw)) throw new Error("not an object");
        return {
          results: arr(raw.result).filter(isObj).slice(0, 10).map((r) => ({ symbol: str(r.symbol, 20), name: str(r.description, 100), type: str(r.type, 40) })),
        };
      },
      exampleParams: { query: "nvidia" },
      example: { results: [{ symbol: "NVDA", name: "NVIDIA CORP", type: "Common Stock" }] },
    },
    profile: {
      description: "A company's name, industry, exchange, market cap and website (found: false when unknown)",
      url: "https://finnhub.io/api/v1/stock/profile2",
      params: [{ key: "symbol", type: "text", required: true, maxLength: 12, upper: true, pattern: "^[A-Za-z][A-Za-z0-9.\\-]*$", description: "Ticker symbol" }],
      cacheSeconds: 86400,
      shape: (raw, p) => {
        if (!isObj(raw) || !str(raw.name)) return { found: false, symbol: p.symbol };
        return {
          found: true,
          symbol: str(raw.ticker, 20) ?? p.symbol,
          name: str(raw.name, 100),
          industry: str(raw.finnhubIndustry, 60),
          exchange: str(raw.exchange, 80),
          country: str(raw.country, 4),
          currency: str(raw.currency, 6),
          marketCapMillions: round(raw.marketCapitalization, 0),
          ipo: str(raw.ipo, 10),
          website: str(raw.weburl, 200),
        };
      },
      exampleParams: { symbol: "NVDA" },
      example: { found: true, symbol: "NVDA", name: "NVIDIA Corp", industry: "Semiconductors", exchange: "NASDAQ NMS - GLOBAL MARKET", country: "US", currency: "USD", marketCapMillions: 4410000, ipo: "1999-01-22", website: "https://www.nvidia.com/" },
    },
    news: {
      description: "Recent news headlines about a company (newest first, at most 10)",
      url: "https://finnhub.io/api/v1/company-news",
      params: [
        { key: "symbol", type: "text", required: true, maxLength: 12, upper: true, pattern: "^[A-Za-z][A-Za-z0-9.\\-]*$", description: "Ticker symbol" },
        { key: "days", type: "number", integer: true, min: 1, max: 30, default: 7, description: "How many days back" },
      ],
      query: (p, now) => ({ symbol: p.symbol!, from: ymd(now - Number(p.days) * 86400_000), to: ymd(now) }),
      cacheSeconds: 900,
      shape: (raw) => ({
        articles: arr(raw).filter(isObj).sort((a, b) => (num(b.datetime) ?? 0) - (num(a.datetime) ?? 0)).slice(0, 10).map((a) => ({
          headline: str(a.headline, 300),
          source: str(a.source, 60),
          url: str(a.url, 500),
          summary: str(a.summary, 600),
          time: num(a.datetime) !== null ? num(a.datetime)! * 1000 : null,
        })),
      }),
      exampleParams: { symbol: "NVDA", days: 7 },
      example: { articles: [{ headline: "Nvidia shares rise ahead of earnings", source: "Reuters", url: "https://www.reuters.com/", summary: "Shares of the chipmaker rose…", time: 1793030400000 }] },
    },
  },
};

export const INTEGRATIONS: Integration[] = [weather, dictionary, finnhub];
export const INTEGRATION_IDS = INTEGRATIONS.map((i) => i.id);

export const integrationById = (id: string): Integration | undefined => INTEGRATIONS.find((i) => i.id === id);

/** Errors an integration call can end with (actions and views get `code` and a sentence). */
export type IntegrationErrorCode = "not_enabled" | "no_key" | "not_declared" | "unknown" | "bad_params" | "rate_limited" | "upstream" | "timeout" | "too_many";

export type IntegrationResult = { ok: true; data: unknown; cached?: boolean } | { ok: false; code: IntegrationErrorCode; message: string };

/** Checks and normalises an endpoint's params: defaults filled, undeclared ones dropped. */
export function checkIntegrationParams(ep: IntegrationEndpoint, raw: unknown): { ok: true; values: ParamValues } | { ok: false; message: string } {
  const input = isObj(raw) ? raw : {};
  const values: ParamValues = {};
  for (const p of ep.params) {
    let v: unknown = Object.hasOwn(input, p.key) ? input[p.key] : undefined;
    if (v === undefined || v === null || v === "") {
      if (p.default !== undefined) v = p.default;
      else if (p.required) return { ok: false, message: `${p.key} is required` };
      else continue;
    }
    if (p.type === "number") {
      const n = typeof v === "number" ? v : typeof v === "string" && v.trim() ? Number(v) : NaN;
      if (!Number.isFinite(n)) return { ok: false, message: `${p.key} must be a number` };
      if (p.integer && !Number.isInteger(n)) return { ok: false, message: `${p.key} must be a whole number` };
      if (p.min !== undefined && n < p.min) return { ok: false, message: `${p.key} must be at least ${p.min}` };
      if (p.max !== undefined && n > p.max) return { ok: false, message: `${p.key} must be at most ${p.max}` };
      values[p.key] = n;
    } else if (p.type === "choice") {
      const s = String(v);
      if (!p.options?.includes(s)) return { ok: false, message: `${p.key} must be one of ${p.options?.join(", ")}` };
      values[p.key] = s;
    } else {
      if (typeof v !== "string" && typeof v !== "number") return { ok: false, message: `${p.key} must be text` };
      let s = String(v).trim();
      if (p.upper) s = s.toUpperCase();
      if (p.lower) s = s.toLowerCase();
      if (!s && p.required) return { ok: false, message: `${p.key} is required` };
      if (s.length > (p.maxLength ?? 200)) return { ok: false, message: `${p.key} is too long (at most ${p.maxLength ?? 200} characters)` };
      if (p.pattern && !new RegExp(p.pattern).test(s)) return { ok: false, message: `${p.key} "${s.slice(0, 40)}" doesn't look right` };
      values[p.key] = s;
    }
  }
  return { ok: true, values };
}

/** The URL for a call (without the key). */
export function integrationUrl(ep: IntegrationEndpoint, p: ParamValues, now: number): string {
  const inPath = new Set<string>();
  const path = ep.url.replace(/\{(\w+)\}/g, (_, k: string) => {
    inPath.add(k);
    return encodeURIComponent(String(p[k] ?? ""));
  });
  const q = ep.query ? ep.query(p, now) : Object.fromEntries(Object.entries(p).filter(([k]) => !inPath.has(k)));
  const u = new URL(path);
  for (const [k, v] of Object.entries(q)) u.searchParams.set(k, String(v));
  return u.toString();
}

/** A stable key for one call (cache, replay): integration, endpoint and the params in order. */
export const integrationCallKey = (integration: string, endpoint: string, p: ParamValues) =>
  `${integration}.${endpoint}?${Object.keys(p).sort().map((k) => `${k}=${encodeURIComponent(String(p[k]))}`).join("&")}`;

/** The sentence for an error, for people. */
export function integrationErrorMessage(code: IntegrationErrorCode, name: string, detail?: string): string {
  switch (code) {
    case "not_enabled": return `${name} isn't turned on in this server: an owner or admin can turn it on in Apps → Integrations.`;
    case "no_key": return `${name} needs an API key: an owner or admin can add one in Apps → Integrations.`;
    case "not_declared": return `This app doesn't use ${name}.`;
    case "unknown": return "There's no such integration.";
    case "bad_params": return `That doesn't work with ${name}: ${detail ?? "check what was asked"}.`;
    case "rate_limited": return `${name} is busy for this server right now; try again in a minute.`;
    case "upstream": return `${name} didn't answer properly${detail ? ` (${detail})` : ""}.`;
    case "timeout": return `${name} took too long to answer.`;
    case "too_many": return "That asked for too much outside data at once.";
  }
}

// ---- For people and the build agent: the catalog without functions ----

export interface IntegrationInfo {
  id: string;
  name: string;
  icon: string;
  description: string;
  homepage: string;
  terms: string;
  auth: { type: "none" } | { type: "api_key"; signup: string; help: string };
  endpoints: { name: string; description: string; params: IntegrationParam[] }[];
}

export const integrationInfo = (i: Integration): IntegrationInfo => ({
  id: i.id,
  name: i.name,
  icon: i.icon,
  description: i.description,
  homepage: i.homepage,
  terms: i.terms,
  auth: i.auth.type === "none" ? { type: "none" } : { type: "api_key", signup: i.auth.signup, help: i.auth.help },
  endpoints: Object.entries(i.endpoints).map(([name, e]) => ({ name, description: e.description, params: e.params })),
});

/** A server's settings for one integration, as members see them (never the key). */
export interface IntegrationSetting {
  id: string;
  enabled: boolean;
  /** Keyed integrations: a key is stored. */
  hasKey: boolean;
  /** Its last 4 characters ("••••1a2b"). */
  keyHint?: string;
  /** The stored key can't be read (the secret changed): enter it again. */
  keyUnreadable?: boolean;
  updatedBy?: string;
  updatedAt?: number;
  /** Ready to call: on, and keyed ones have a key. */
  ready: boolean;
  /** Upstream calls in the last 7 days (cache hits not counted), and errors. */
  calls7d?: number;
  errors7d?: number;
}

/** The Integrations section of the build agent's guide, made from the catalog. */
export function integrationsGuide(): string {
  const lines: string[] = [];
  for (const i of INTEGRATIONS) {
    lines.push(`### ${i.id}: ${i.name}${i.auth.type === "api_key" ? " (needs the server's API key)" : ""}`, i.description, "");
    for (const [name, e] of Object.entries(i.endpoints)) {
      const ps = e.params.map((p) => `${p.key}${p.required ? "" : "?"}: ${p.type === "choice" ? p.options!.map((o) => JSON.stringify(o)).join(" | ") : p.type}${p.default !== undefined ? ` = ${JSON.stringify(p.default)}` : ""}`).join(", ");
      lines.push(`- \`${i.id}.${name}({ ${ps} })\`: ${e.description}.`);
      lines.push(`  Example: \`ctx.integration("${i.id}", "${name}", ${JSON.stringify(e.exampleParams)})\` → \`${JSON.stringify(e.example)}\``);
    }
    lines.push("");
  }
  return lines.join("\n");
}

/**
 * What @Bloom knows about integrations in this server (agent/prompt.ts): every catalog entry, whether it's ready here
 * (or off, or waiting for a key), which apps use it, and where and by whom it's set up. Facts only, no keys or hints.
 */
export function describeIntegrationsForBloom(o: { settings: IntegrationSetting[]; apps: { slug: string; name: string; integrations?: string[] }[]; setupUrl: string; admins: string[] }): string {
  const lines = INTEGRATIONS.map((i) => {
    const s = o.settings.find((x) => x.id === i.id);
    const status = s?.ready
      ? "ON: apps that use it work"
      : s?.enabled
        ? `TURNED ON BUT WAITING FOR AN API KEY: an owner or admin pastes the server's own free key (from ${i.auth.type === "api_key" ? i.auth.signup : "the service"})`
        : `OFF: an owner or admin turns it on${i.auth.type === "api_key" ? ` and pastes a free API key (from ${i.auth.signup})` : " (no key needed)"}`;
    const usedBy = o.apps.filter((a) => a.integrations?.includes(i.id)).map((a) => `${a.name} (/${a.slug})`);
    const endpoints = Object.entries(i.endpoints).map(([n, e]) => `${n}: ${e.description}`).join("; ");
    return `- ${i.name} [${i.id}], ${i.auth.type === "api_key" ? "needs a free API key" : "free, no key"}. ${i.description}. Can do: ${endpoints}. Status: ${status}. Used by: ${usedBy.join(", ") || "no apps yet"}.`;
  });
  return `${lines.join("\n")}
Set up at Apps → Integrations: ${o.setupUrl} (anyone can look; only owners and admins can change it${o.admins.length ? `; here that's ${o.admins.join(", ")}` : ""}).`;
}
